import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, setBearer, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, obj = authed
  const [error, setError] = useState("");

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setBearer(data.access_token);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // network failures on logout shouldn't block clearing local state; log for visibility
      // eslint-disable-next-line no-console
      console.warn("Logout API call failed (clearing local session anyway):", e?.message || e);
    }
    setBearer(null);
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, error, login, logout, refresh: bootstrap }),
    [user, error, login, logout, bootstrap],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
